import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { UserPlus, Copy } from "lucide-react";

export default function CoachStudents() {
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", skill_level: 3.0, goals: "" });
  const [saving, setSaving] = useState(false);
  const [createdCreds, setCreatedCreds] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/students");
      setStudents(data || []);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const filtered = students.filter((s) =>
    (s.name || "").toLowerCase().includes(q.toLowerCase()) ||
    (s.email || "").toLowerCase().includes(q.toLowerCase())
  );

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/students", {
        name: form.name, email: form.email,
        skill_level: parseFloat(form.skill_level) || 3.0,
        goals: form.goals,
      });
      toast.success("Student added");
      setCreatedCreds({ email: data.email, password: data.temp_password, name: data.name });
      setForm({ name: "", email: "", skill_level: 3.0, goals: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    }
    setSaving(false);
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch (_) {}
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="coach-students-page" className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Roster</div>
          <h2 className="font-display text-4xl font-black uppercase tracking-tighter mt-2">Your Students</h2>
          <p className="text-sm text-[#A3A3A3] mt-2">Add students anytime — we'll generate a login and email them an invite.</p>
        </div>
        <div className="flex gap-3">
          <input
            placeholder="Search name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none px-4 py-3 text-sm w-64"
            data-testid="students-search-input"
          />
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setCreatedCreds(null); }}>
            <DialogTrigger asChild>
              <button className="btn-primary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="add-student-button">
                <UserPlus size={14} /> Add student
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-[#141414] border-white/10 max-w-lg" data-testid="add-student-dialog">
              <DialogHeader>
                <DialogTitle className="font-display uppercase tracking-tight text-2xl">Add a student</DialogTitle>
              </DialogHeader>
              {createdCreds ? (
                <div className="space-y-4" data-testid="student-created-credentials">
                  <div className="text-sm text-white/90">
                    <strong>{createdCreds.name}</strong> was added. Share these temporary credentials (also emailed):
                  </div>
                  <CredRow label="Email" value={createdCreds.email} onCopy={() => copy(createdCreds.email)} />
                  <CredRow label="Temp password" value={createdCreds.password} onCopy={() => copy(createdCreds.password)} />
                  <p className="text-xs text-[#A3A3A3]">
                    Ask them to change the password on first login (Settings → change password — coming soon; for now, note it down).
                  </p>
                  <div className="flex gap-2 pt-2">
                    <button onClick={() => setCreatedCreds(null)} className="btn-secondary px-4 py-2 text-xs" data-testid="add-another-button">
                      Add another
                    </button>
                    <button onClick={() => { setCreatedCreds(null); setOpen(false); }} className="btn-primary px-4 py-2 text-xs" data-testid="close-credentials-button">
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={create} className="space-y-4">
                  <Field label="Full name" value={form.name} onChange={set("name")} required testId="new-student-name" />
                  <Field label="Email" type="email" value={form.email} onChange={set("email")} required testId="new-student-email" />
                  <Field label="Current skill (DUPR, approx)" type="number" step="0.1" min={1.0} max={6.0}
                         value={form.skill_level} onChange={set("skill_level")} testId="new-student-skill" />
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Goals (optional)</label>
                    <textarea
                      rows={2}
                      value={form.goals}
                      onChange={set("goals")}
                      placeholder="e.g. Improve 3rd-shot drops"
                      className="w-full bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none p-3 text-sm mt-1"
                      data-testid="new-student-goals"
                    />
                  </div>
                  <div className="text-xs text-[#A3A3A3] border-l-2 border-[#38BDF8] pl-3 py-1">
                    We'll auto-generate a temporary password and email them an invite.
                  </div>
                  <button disabled={saving} className="btn-primary w-full py-3 text-sm" data-testid="new-student-submit">
                    {saving ? "Creating…" : "Create student"}
                  </button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-white/10 p-12 text-center text-[#A3A3A3]" data-testid="students-empty-state">
          No students yet. Hit <strong className="text-white">Add student</strong> to onboard your first player.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <Link
              key={s.user_id}
              to={`/coach/students/${s.user_id}`}
              className="card-surface p-6 block"
              data-testid={`student-card-${s.user_id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-2xl font-black uppercase tracking-tight">{s.name}</div>
                  <div className="text-xs text-[#A3A3A3] mt-1">{s.email}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold">DUPR</div>
                  <div className="font-display text-3xl font-black">{(s.skill_level ?? 0).toFixed ? s.skill_level.toFixed(1) : s.skill_level}</div>
                </div>
              </div>
              <div className="mt-4 text-sm text-[#A3A3A3] line-clamp-2">{s.goals || "No goals set."}</div>
              <div className="mt-5 flex items-center justify-between text-xs text-white/70">
                <span>{s.lessons_count || 0} lessons</span>
                <span className="text-[#38BDF8] font-bold uppercase tracking-widest">Open →</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, testId, step, min, max }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">{label}</label>
      <input
        type={type} value={value} onChange={onChange} required={required}
        step={step} min={min} max={max}
        className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
        data-testid={testId}
      />
    </div>
  );
}

function CredRow({ label, value, onCopy }) {
  return (
    <div className="flex items-center justify-between border border-white/10 px-3 py-2 bg-white/[0.02]">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">{label}</div>
        <div className="font-mono text-sm mt-0.5">{value}</div>
      </div>
      <button type="button" onClick={onCopy} className="p-2 hover:bg-white/5" aria-label={`Copy ${label}`}>
        <Copy size={14} />
      </button>
    </div>
  );
}
